import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/client';

export const AdminCommissionsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [editForm, setEditForm] = useState({
    loadInCommission: '',
    withdrawalCommission: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCommissionSettingsAll();
      setSettings(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load commission settings');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setSelectedSetting(setting);
    setEditForm({
      loadInCommission: setting.load_in_commission,
      withdrawalCommission: setting.withdrawal_commission,
      referralCommission: setting.referral_commission,
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!editForm.loadInCommission || !editForm.withdrawalCommission || editForm.referralCommission === '') {
      setError('All commission rates are required');
      return;
    }
    try {
      await adminAPI.updateCommissionSettings({
        userId: selectedSetting.user_id,
        loadInCommission: parseFloat(editForm.loadInCommission),
        withdrawalCommission: parseFloat(editForm.withdrawalCommission),
        referralCommission: parseFloat(editForm.referralCommission),
      });
      setShowEditModal(false);
      fetchSettings();
    } catch (err) {
      setError('Failed to save commission settings');
    }
  };

  const getSettingLabel = (setting) => {
    return setting.user_id ? `User ID: ${setting.user_id} (Override)` : 'Global Default';
  };

  const getSettingBadge = (setting) => {
    return setting.user_id ? (
      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
        User Override
      </span>
    ) : (
      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
        Global Default
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading commission settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-600">Commission Settings</h1>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-gray-800"
          >
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto py-8 px-4">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Info Card */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6">
          <p className="text-sm text-blue-900">
            <strong>📋 How it works:</strong> Global defaults apply to all users. User-specific overrides take precedence for individual users.
          </p>
        </div>

        {/* Commission Settings Cards */}
        <div className="space-y-4">
          {settings.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              No commission settings found
            </div>
          ) : (
            settings.map((setting) => (
              <div
                key={setting.id}
                className="bg-white rounded-lg shadow-lg p-6 flex justify-between items-center hover:shadow-xl transition"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {getSettingLabel(setting)}
                  </h3>
                  {getSettingBadge(setting)}
                  <div className="mt-4 space-y-2 text-sm">
                    <p className="text-gray-600">
                      <strong>Load In Commission:</strong> <span className="text-blue-600 font-semibold">{setting.load_in_commission}%</span>
                    </p>
                    <p className="text-gray-600">
                      <strong>Withdrawal Commission:</strong> <span className="text-green-600 font-semibold">{setting.withdrawal_commission}%</span>
                    </p>
                    {setting.referral_commission !== null && (
                      <p className="text-gray-600">
                        <strong>Referral Commission:</strong> <span className="text-purple-600 font-semibold">{setting.referral_commission}%</span>
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(setting)}
                  className="ml-6 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition h-fit"
                >
                  Edit
                </button>
              </div>
            ))
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">💡 Instructions</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• <strong>Global Default:</strong> Applies to all users by default</li>
            <li>• <strong>User Override:</strong> Creates a custom rate for a specific user</li>
            <li>• <strong>Load In Commission:</strong> % charged when users add money to wallet</li>
            <li>• <strong>Withdrawal Commission:</strong> % charged when users withdraw money</li>
            <li>• <strong>Referral Commission:</strong> % earned from referred user transactions</li>
            <li>• All percentages are applied to the transaction amount</li>
          </ul>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedSetting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Edit: {getSettingLabel(selectedSetting)}
            </h2>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Load In Commission (%)</label>
                <input
                  type="number"
                  value={editForm.loadInCommission}
                  onChange={(e) => setEditForm(prev => ({ ...prev, loadInCommission: e.target.value }))}
                  placeholder="e.g., 2.5"
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Withdrawal Commission (%)</label>
                <input
                  type="number"
                  value={editForm.withdrawalCommission}
                  onChange={(e) => setEditForm(prev => ({ ...prev, withdrawalCommission: e.target.value }))}
                  placeholder="e.g., 10"
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referral Commission (%)</label>
                <input
                  type="number"
                  value={editForm.referralCommission}
                  onChange={(e) => setEditForm(prev => ({ ...prev, referralCommission: e.target.value }))}
                  placeholder="e.g., 2"
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommissionsPage;
